import { useUserPreferences } from "~/hooks/useUserPreferences";

export interface UseFavoritesResult<T> {
  isFavorite: (id: string) => boolean;
  toggleFavorite: (id: string) => void;
  favorites: T[];
  others: T[];
}

/**
 * Partitions `items` into favorites and the rest, persisting the favorite ids
 * to localStorage. Each group keeps the order of `items`, so a caller that
 * sorts first gets a sorted Favorites section for free.
 *
 * A stored id with no matching item is omitted from the result, so a deleted
 * or no-longer-visible resource never renders. The id is left in storage
 * rather than pruned: `items` may be a subset (e.g. filtered by search), and
 * pruning here would wipe favorites for resources that are merely filtered
 * out, not gone. Orphaned ids are harmless — they are uuids and never reused.
 */
export function useFavorites<T>(
  storageKey: string,
  items: T[],
  getId: (item: T) => string
): UseFavoritesResult<T> {
  const [preferences, updatePreferences] = useUserPreferences();
  const favoriteSet = new Set(preferences.favoriteItems?.[storageKey] ?? []);

  const isFavorite = (id: string) => favoriteSet.has(id);

  // Derive the next list from current storage, not this instance's snapshot, so
  // a concurrent write under favoriteItems (another tab or list key) survives.
  const toggleFavorite = (id: string) =>
    updatePreferences((current) => {
      const ids = current.favoriteItems?.[storageKey] ?? [];
      const next = ids.includes(id) ? ids.filter((f) => f !== id) : [...ids, id];
      return { favoriteItems: { ...current.favoriteItems, [storageKey]: next } };
    });

  const favorites = items.filter((item) => favoriteSet.has(getId(item)));
  const others = items.filter((item) => !favoriteSet.has(getId(item)));

  return { isFavorite, toggleFavorite, favorites, others };
}
